import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Crown, CheckCircle2, Loader2, Info, Timer, Eye, EyeOff, Rocket } from "lucide-react";
import { FounderPriceBlock } from "@/components/FounderPriceBlock";

const EMPTY = { business_name: "", contact_name: "", email: "", password: "", phone: "", city: "", offer_proposed: "", message: "", gdpr_consent: false };

const INCLUDED = [
  "Mini-site local référencé Google / SEO complet",
  "Offres & promotions illimitées",
  "Espace Clients : CRM + campagnes e-mail avec IA",
  "Outils IA de rédaction & génération d'images",
  "Mise en avant prioritaire + statistiques avancées",
  "Jusqu'à 2 publications Facebook/mois (après validation)",
];

export default function FounderOffer() {
  useSeo({
    title: "Offre Fondateurs — 12 mois gratuits pour les pros de l'Eure-et-Loir",
    description: "Activez votre place de Partenaire Fondateur sur PROXIreduc-28 : accès gratuit pendant 12 mois à tous les outils (mini-site, carte digitale, CRM, IA), sans carte bancaire et sans engagement.",
    canonicalPath: "/offre-fondateurs",
    image: "/og-image.jpg",
  });
  const [status, setStatus] = useState(null);
  const [places, setPlaces] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    api.get("/founders/campaign").then((r) => setStatus(r.data)).catch(() => setStatus({ status: "open" }));
    api.get("/founders/status").then((r) => setPlaces(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Merci d'accepter la politique de confidentialité."); return; }
    const alreadyPro = user && user.role === "professional";
    if (!alreadyPro && (form.password || "").length < 6) {
      toast.error("Votre mot de passe doit contenir au moins 6 caractères."); return;
    }
    setSending(true);
    try {
      if (!alreadyPro) {
        const parts = (form.contact_name || "").trim().split(" ");
        await register({
          email: form.email, password: form.password, role: "professional",
          first_name: parts[0] || form.contact_name || "", last_name: parts.slice(1).join(" "),
          city: form.city || "", business_name: form.business_name,
          business_phone: form.phone || "", offer_proposed: form.offer_proposed || "",
        });
      }
      try {
        await api.post("/founders/activate", {});
        toast.success("Votre place Partenaire Fondateur est activée — 0 € pendant 12 mois !");
      } catch (actErr) {
        toast("Votre compte est créé. L'activation Fondateur n'a pas pu aboutir (places épuisées ?) — vous pourrez choisir une formule depuis votre espace.");
      }
      navigate("/pro?welcome=1");
    } catch (err) {
      const msg = formatApiError(err);
      if (/déjà utilisé/i.test(msg)) {
        toast.error("Un compte existe déjà avec cet email. Connectez-vous pour activer votre place.");
        navigate("/connexion?next=/offre-fondateurs");
      } else { toast.error(msg); }
      setSending(false);
    }
  };

  const closed = status?.status === "closed";
  const endDate = status?.end ? new Date(status.end).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
  const remaining = places?.remaining ?? null;
  const total = places?.total ?? 50;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="founder-offer-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-widest">
          <Crown size={14}/> Offre de lancement
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Devenez Partenaire Fondateur</h1>
        <p className="mt-3 text-slate-600 text-base max-w-xl mx-auto">
          Rejoignez les tout premiers commerces du réseau PROXIreduc-28 : <b>0 € pendant 12 mois</b> pour les 50 premiers partenaires activés à Chartres et dans son agglomération. Sans carte bancaire · sans engagement.
        </p>
      </div>

      {/* Offre de lancement */}
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="founder-counter">
        {!status ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18}/> Chargement…</div>
        ) : (
          <>
            <FounderPriceBlock variant="light" />
            {endDate && <div className="mt-3 text-xs text-slate-500 text-center">{closed ? "Offre clôturée." : `Activation possible jusqu'au ${endDate}.`}</div>}

            <div className="mt-3 text-[11px] text-slate-500">Activation gratuite et immédiate, sans carte bancaire. Aucune facturation automatique : rien ne se passe à la fin des 12 mois sans votre accord. Sans engagement. TVA non applicable, art. 293 B du CGI.</div>

            {status.status === "open" && status.end && (() => {
              const diff = Math.max(0, new Date(status.end).getTime() - nowTs);
              const dd = Math.floor(diff / 86400000);
              const hh = Math.floor((diff % 86400000) / 3600000);
              const mm = Math.floor((diff % 3600000) / 60000);
              const ss = Math.floor((diff % 60000) / 1000);
              const cell = (v, l) => (
                <div key={l} className="flex flex-col items-center bg-slate-900 text-white rounded-xl px-3 py-2 min-w-[58px]">
                  <span className="font-heading text-2xl font-extrabold tabular-nums">{String(v).padStart(2, "0")}</span>
                  <span className="text-[9px] uppercase tracking-widest text-white/60 font-bold">{l}</span>
                </div>
              );
              return (
                <div className="mt-4 rounded-2xl bg-[#E05638]/5 border border-[#E05638]/20 p-4" data-testid="founder-countdown">
                  <div className="flex items-center gap-1.5 text-[#E05638] text-xs font-bold uppercase tracking-widest">
                    <Timer size={14} /> L'offre de lancement se termine dans
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {cell(dd, "jours")}{cell(hh, "h")}{cell(mm, "min")}{cell(ss, "sec")}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">Rejoignez nos premiers partenaires avant la fin de l'offre de lancement.</div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Avantages */}
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        {INCLUDED.map((a, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-2">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5"/>
            <span className="text-sm text-slate-700">{a}</span>
          </div>
        ))}
      </div>

      {/* Formulaire */}
      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="founder-form">
        <div className="flex items-center gap-2 font-heading font-bold text-slate-900"><Sparkles size={18} className="text-[#E05638]"/> Activer ma place fondateur</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nom du commerce</Label><Input required value={form.business_name} onChange={set("business_name")} data-testid="founder-business"/></div>
          <div><Label>Votre nom</Label><Input required value={form.contact_name} onChange={set("contact_name")} data-testid="founder-contact"/></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="founder-email"/></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} data-testid="founder-phone"/></div>
        </div>
        {!(user && user.role === "professional") && (
          <div>
            <Label>Mot de passe (min. 6 caractères)</Label>
            <div className="mt-1 relative">
              <Input type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} className="pr-10" data-testid="founder-password"/>
              <button type="button" onClick={()=>setShowPwd(v=>!v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                      aria-label={showPwd ? "Masquer" : "Afficher"} data-testid="founder-password-toggle">
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>
        )}
        <div><Label>Ville</Label><Input value={form.city} onChange={set("city")} data-testid="founder-city"/></div>
        <div><Label>Offre que vous aimeriez proposer (optionnel)</Label><Textarea rows={2} value={form.offer_proposed} onChange={set("offer_proposed")} data-testid="founder-offer"/></div>
        <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.gdpr_consent} onChange={(e)=>setForm((f)=>({...f, gdpr_consent: e.target.checked}))} className="mt-0.5 w-4 h-4" data-testid="founder-gdpr"/>
          J'accepte la politique de confidentialité et les conditions de l'offre.
        </label>
        <Button type="submit" disabled={sending || closed} className="w-full btn-shine text-white h-11" data-testid="founder-submit">
          {sending ? "Activation…" : closed ? "Offre clôturée" : "Activer ma place fondateur — 0 €"}
        </Button>
        {!closed && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[#E05638]/5 border border-[#E05638]/20" data-testid="founder-space-note">
            <Info size={16} className="text-[#E05638] shrink-0 mt-0.5"/>
            <span className="text-xs text-slate-600 leading-relaxed">
              Pas besoin de tout remplir maintenant : votre <strong>espace professionnel</strong> est créé immédiatement. Vous pourrez y compléter votre mini-site (adresse, horaires, catégorie, photos, réseaux sociaux, site internet…) et publier vos offres quand vous le souhaitez.
            </span>
          </div>
        )}
        {!closed && (
          <p className="text-center text-[11px] text-slate-500" data-testid="founder-pay-hint">
            Activation gratuite et immédiate · sans carte bancaire · sans engagement. Vous accédez directement à votre espace.
          </p>
        )}
      </form>
    </div>
  );
}
